import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Crown, CheckCircle, XCircle, Clock, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SearchBar from '@/components/shared/SearchBar';
import { API, toast } from '@/App';

const ManageSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ active: 0, pending: 0, expired: 0 });

  // Filtered subscriptions based on search
  const filteredSubscriptions = useMemo(() => {
    if (!searchQuery) return subscriptions;
    
    const query = searchQuery.toLowerCase();
    return subscriptions.filter(sub => 
      sub.vendeur_name.toLowerCase().includes(query) ||
      sub.plan.toLowerCase().includes(query) ||
      sub.payment_method.toLowerCase().includes(query) ||
      sub.transaction_id?.toLowerCase().includes(query)
    );
  }, [subscriptions, searchQuery]);

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscriptions`);
      const subs = response.data;
      setSubscriptions(subs);
      
      // Calculate stats
      setStats({
        active: subs.filter(s => s.status === 'active').length,
        pending: subs.filter(s => s.status === 'pending').length,
        expired: subs.filter(s => s.status === 'expired').length
      });
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (subscriptionId) => {
    if (!window.confirm('Confirmer l\'activation de cet abonnement ?')) return;

    try {
      await axios.patch(`${API}/admin/subscriptions/${subscriptionId}/activate`);
      toast.success('Abonnement activé avec succès');
      loadSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    }
  };

  const getPlanLabel = (plan) => {
    const labels = {
      'starter_1m': 'Starter (1 mois)',
      'pro_3m': 'Pro (3 mois)',
      'business_12m': 'Business (12 mois)'
    };
    return labels[plan] || plan;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': { label: 'Actif', class: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3 h-3" /> },
      'pending': { label: 'En attente', class: 'bg-amber-100 text-amber-700', icon: <Clock className="w-3 h-3" /> },
      'expired': { label: 'Expiré', class: 'bg-red-100 text-red-700', icon: <XCircle className="w-3 h-3" /> }
    };
    const info = statusMap[status] || statusMap['pending'];
    return (
      <Badge className={`${info.class} flex items-center gap-1 w-fit`}>
        {info.icon}
        {info.label}
      </Badge>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-t-4 border-t-green-500">
          <CardHeader className="pb-3">
            <CardDescription>Abonnements Actifs</CardDescription>
            <CardTitle className="text-3xl">{stats.active}</CardTitle>
          </CardHeader>
        </Card>

        <Card className="border-t-4 border-t-amber-500">
          <CardHeader className="pb-3">
            <CardDescription>En attente validation</CardDescription>
            <CardTitle className="text-3xl">{stats.pending}</CardTitle>
          </CardHeader>
        </Card>

        <Card className="border-t-4 border-t-red-500">
          <CardHeader className="pb-3">
            <CardDescription>Expirés</CardDescription>
            <CardTitle className="text-3xl">{stats.expired}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Subscriptions List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-500" />
            Gestion des abonnements Pro
          </CardTitle>
          <CardDescription>Gérer tous les abonnements Coli Noki Pro</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search Bar */}
          {subscriptions.length > 0 && (
            <div className="mb-6">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Rechercher par vendeur, plan, méthode de paiement ou transaction..."
                className="max-w-lg"
              />
              {searchQuery && (
                <p className="text-sm text-gray-600 mt-2">
                  {filteredSubscriptions.length} résultat(s) trouvé(s)
                </p>
              )}
            </div>
          )}

          <Tabs defaultValue="pending">
            <TabsList>
              <TabsTrigger value="pending">En attente ({stats.pending})</TabsTrigger>
              <TabsTrigger value="active">Actifs ({stats.active})</TabsTrigger>
              <TabsTrigger value="all">Tous</TabsTrigger>
            </TabsList>

            <TabsContent value="pending">
              {filteredSubscriptions.filter(s => s.status === 'pending').length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun abonnement en attente</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendeur</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Paiement</TableHead>
                      <TableHead>Transaction</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSubscriptions.filter(s => s.status === 'pending').map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell className="font-medium">{sub.vendeur_name}</TableCell>
                        <TableCell>{getPlanLabel(sub.plan)}</TableCell>
                        <TableCell className="font-semibold">{sub.amount} {sub.currency}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{sub.payment_method}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{sub.transaction_id || 'N/A'}</TableCell>
                        <TableCell className="text-sm">
                          {new Date(sub.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            data-testid={`activate-sub-${sub.id}`}
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleActivate(sub.id)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Activer
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="active">
              {filteredSubscriptions.filter(s => s.status === 'active').length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Crown className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun abonnement actif</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendeur</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Début</TableHead>
                      <TableHead>Expiration</TableHead>
                      <TableHead>Montant</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSubscriptions.filter(s => s.status === 'active').map((sub) => (
                      <TableRow key={sub.id}>
                        <TableCell className="font-medium">{sub.vendeur_name}</TableCell>
                        <TableCell>{getPlanLabel(sub.plan)}</TableCell>
                        <TableCell className="text-sm">
                          {sub.start_date ? new Date(sub.start_date).toLocaleDateString('fr-FR') : 'N/A'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {sub.end_date ? new Date(sub.end_date).toLocaleDateString('fr-FR') : 'N/A'}
                        </TableCell>
                        <TableCell className="font-semibold">{sub.amount} {sub.currency}</TableCell>
                        <TableCell>{getStatusBadge(sub.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="all">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendeur</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Paiement</TableHead>
                    <TableHead>Date création</TableHead>
                    <TableHead>Expiration</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubscriptions.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell className="font-medium">{sub.vendeur_name}</TableCell>
                      <TableCell>{getPlanLabel(sub.plan)}</TableCell>
                      <TableCell className="font-semibold">{sub.amount} {sub.currency}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{sub.payment_method}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(sub.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell className="text-sm">
                        {sub.end_date ? new Date(sub.end_date).toLocaleDateString('fr-FR') : 'N/A'}
                      </TableCell>
                      <TableCell>{getStatusBadge(sub.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageSubscriptions;
